// Centralized local storage management with error handling and validation
const STORAGE_KEY = 'planningWithPurpose';
const STORAGE_VERSION = '1.0';

// Default state structure
const defaultState = {
  version: STORAGE_VERSION,
  access: {
    granted: false,
    date: null
  },
  onboarding: {
    completed: false,
    authorName: '',
    experience: '',
    goals: []
  },
  bookVision: {
    title: '',
    type: '',
    why: '',
    audience: '',
    transformation: '',
    verse: ''
  },
  structure: {
    type: 'nonfiction',
    fiction: {
      act1: '',
      act2: '',
      act3: '',
      act4: '',
      scenes: []
    },
    nonfiction: {
      before: '',
      after: '',
      chapters: []
    }
  },
  scope: {
    wordCount: '50000',
    chapterCount: '10',
    timeline: '90',
    writingDays: 5,
    schedule: '',
    obstacles: ''
  },
  kickstart: {
    startingIdea: '',
    firstParagraph: '',
    connection: ''
  },
  progress: {
    completedSections: [],
    lastVisit: null
  }
};

class LocalStorageManager {
  constructor() {
    this.isAvailable = this.checkAvailability();
    this.migrate();
  }

  checkAvailability() {
    try {
      const test = '__localStorage_test__';
      localStorage.setItem(test, test);
      localStorage.removeItem(test);
      return true;
    } catch (e) {
      console.warn('LocalStorage not available:', e);
      return false;
    }
  }

  migrate() {
    if (!this.isAvailable) return;
    
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        const data = JSON.parse(stored);
        // Check if migration is needed
        if (!data.version || data.version !== STORAGE_VERSION) {
          console.log('Migrating local storage to version', STORAGE_VERSION);
          this.saveData({ ...defaultState, ...data, version: STORAGE_VERSION });
        }
      }
    } catch (e) {
      console.error('Migration failed, resetting storage:', e);
      this.reset();
    }
  }

  getData() {
    if (!this.isAvailable) return defaultState;
    
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (!stored) return defaultState;
      
      const data = JSON.parse(stored);
      // Merge with defaults to ensure all properties exist
      return this.deepMerge(defaultState, data);
    } catch (e) {
      console.error('Error reading from localStorage:', e);
      return defaultState;
    }
  }

  saveData(data) {
    if (!this.isAvailable) return false;
    
    try {
      const mergedData = this.deepMerge(this.getData(), data);
      mergedData.progress.lastVisit = new Date().toISOString();
      localStorage.setItem(STORAGE_KEY, JSON.stringify(mergedData));
      return true;
    } catch (e) {
      console.error('Error saving to localStorage:', e);
      return false;
    }
  }

  updateSection(section, data) {
    const currentData = this.getData();
    const updatedData = {
      ...currentData,
      [section]: { ...currentData[section], ...data }
    };
    return this.saveData(updatedData);
  }

  markSectionComplete(section) {
    const currentData = this.getData();
    const completedSections = [...new Set([...currentData.progress.completedSections, section])];
    return this.saveData({
      progress: { ...currentData.progress, completedSections }
    });
  }

  reset() {
    if (!this.isAvailable) return;
    
    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch (e) {
      console.error('Error clearing localStorage:', e);
    }
  }

  deepMerge(target, source) {
    const result = { ...target };
    
    for (const key in source) {
      if (source[key] && typeof source[key] === 'object' && !Array.isArray(source[key])) {
        result[key] = this.deepMerge(result[key] || {}, source[key]);
      } else {
        result[key] = source[key];
      }
    }
    
    return result;
  }

  // Utility methods for specific data
  isAccessGranted() {
    return this.getData().access.granted;
  }

  isOnboardingComplete() {
    return this.getData().onboarding.completed;
  }

  getProgress() {
    const data = this.getData();
    return {
      completedSections: data.progress.completedSections,
      totalSections: 5,
      percentage: Math.round((data.progress.completedSections.length / 5) * 100)
    };
  }
}

// Export singleton instance
export const storage = new LocalStorageManager();
export default storage;